import { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { getApiUrl } from "@/utils/api";
import PageShell from "@/components/PageShell";
import { Loader2 } from "lucide-react";
import ModularPage from "./ModularPage";
import CustomPageBody from "@/components/CustomPageBody";

const API = getApiUrl();

export default function CustomStaticPage() {
  const { slug } = useParams();
  const location = useLocation();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModularPage, setIsModularPage] = useState(false);
  // Set right before we kick off `window.location.href` for an agent-login
  // redirect — `finally` still runs (setLoading(false)) after a `return`
  // inside the catch block, so without this guard the component renders
  // one more time with page=null before the browser actually navigates
  // away, crashing CustomPageBody on `page.title_style`.
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    fetchPage();
  }, [slug]);

  // Lazy-load the chosen Google Font for the custom page title if it isn't
  // already loaded by Site Styles (headingFont/bodyFont/navFont).
  useEffect(() => {
    const family = page?.title_style?.fontFamily;
    if (!family) return;
    const id = `slh-title-font-${family.replace(/\s+/g, "-")}`;
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?family=${family.replace(/ /g, "+")}:wght@300;400;500;600;700;800&display=swap`;
    document.head.appendChild(link);
  }, [page?.title_style?.fontFamily]);

  const fetchPage = async () => {
    try {
      setLoading(true);
      setError(null);

      // Modular pages take priority over custom static pages. (Not routed
      // through the shared pageCompositionCache used by <ModularPage> below
      // — nesting that cache's pre-check here caused an intermittent stuck
      // render in testing; <ModularPage>'s own fetch still benefits from it.)
      await axios.get(`${API}/page-composition/${slug}`);
      setIsModularPage(true);
    } catch (modularError) {
      if (modularError.response?.status === 401 && modularError.response?.data?.detail === "agent_login_required") {
        redirectToAgentLogin();
        return;
      }
      if (modularError.response?.status !== 404) {
        console.error("Failed to fetch page:", modularError);
        setError("Failed to load page");
        setLoading(false);
        return;
      }
      try {
        const response = await axios.get(`${API}/custom-static-page/${slug}`);
        setPage(response.data);
        setIsModularPage(false);
      } catch (customError) {
        if (customError.response?.status === 401 && customError.response?.data?.detail === "agent_login_required") {
          redirectToAgentLogin();
          return;
        }
        if (customError.response?.status === 404) {
          // Neither a modular nor a custom page exists — let ModularPage's
          // own fetch render its "Page not found" screen for a consistent UX.
          setIsModularPage(true);
        } else {
          console.error("Failed to fetch page:", customError);
          setError("Failed to load page");
        }
      }
    } finally {
      setLoading(false);
    }
  };

  // Page is gated behind an agent login — send the visitor to sign in, then
  // bounce them back to this exact page once authenticated.
  const redirectToAgentLogin = () => {
    setRedirecting(true);
    const redirectTarget = window.location.pathname + window.location.search;
    window.location.href = `/property-admin/login?redirect=${encodeURIComponent(redirectTarget)}`;
  };

  if (redirecting) {
    return (
      <PageShell>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </PageShell>
    );
  }

  // If it's a modular page, render ModularPage component
  if (loading) {
    return (
      <PageShell>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </PageShell>
    );
  }

  if (error) {
    return (
      <PageShell>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">404</h1>
            <p className="text-xl text-muted-foreground">{error}</p>
          </div>
        </div>
      </PageShell>
    );
  }

  if (isModularPage) {
    return <ModularPage />;
  }

  return <CustomPageBody page={page} />;
}
